import { useState, useEffect } from "react";
import Modal from "../../../components/ui/modal";
import Dropdown from "../../../components/ui/dropdown";
import colors from "../../../utils/colors";
import DateInput from "../../../components/dateInput";
import Button from "../../../components/ui/button";
import { displayErrorMsg } from "../../../utils/displayErrorMsg";
import { TRescheduleAppointmentDetails, TKey } from "../../../types";
import useValidateStateObj from "../../../hooks/useValidateStateObj";
import { updateStateValue } from "../utils/data";
import { useRescheduleAppointment } from "../../../apis/doctor";
import { useAppSelector } from "../../../hooks/useAppSelector";
import moment from "moment";

interface IProps {
  isOpen: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  notes: string;
  address: string;
  appointment: any;
}

const RescheduleAppointmentModal = ({
  isOpen,
  onClose,
  title,
  notes,
  address,
  appointment,
}: IProps) => {
  const [submitting, setSubmitting] = useState(false);

  const [rescheduleAppointmentDetails, setRescheduleAppointmentDetails] =
    useState<TRescheduleAppointmentDetails>({
      appointmentType: { category: "Initial", mode: "Online" }, // ✅ Default values
    } as TRescheduleAppointmentDetails);

  const { isValid } = useValidateStateObj<TRescheduleAppointmentDetails>(
    rescheduleAppointmentDetails
  );
  const { _id: doctorId } = useAppSelector((state) => state.auth.doctorInfo);

  const updateState = (name: TKey, value: string | object) => {
    setRescheduleAppointmentDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearInputs = () => {
    setRescheduleAppointmentDetails({
      ...updateStateValue,
      appointmentType: { category: "Initial", mode: "Online" },
      appointmentStatus: "Active",
      notes,
      address,
    });
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose(false);
    clearInputs();
  };

  const { mutate: rescheduleAppointment, isPending } = useRescheduleAppointment(
    { handleSuccess: handleClose }
  );

  useEffect(() => {
    if (!appointment) return;

    const selectedDate = new Date(appointment["Date"]);

    const startTime = moment(appointment["Time"]?.split(" - ")?.[0], [
      "h:mm A",
    ]).toDate();
    const endTime = moment(appointment["Time"]?.split(" - ")?.[1], [
      "h:mm A",
    ]).toDate();

    // Ensure times inherit the selected date
    if (startTime) {
      startTime.setFullYear(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
    }

    if (endTime) {
      endTime.setFullYear(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
    }

    setRescheduleAppointmentDetails((prev) => ({
      ...prev,
      appointmentId: appointment["Appointment ID"],
      initialAppointmentId: appointment["Initial Appointment ID"],
      appointmentType: {
        category: appointment["Category"] ?? "Initial",
        mode: appointment["Mode"] ?? "Online",
      },
      date: new Date(appointment["Date"]),
      patientId: appointment["Patient ID"],
      doctorName: appointment["Doctor"],
      // Keep user-selected status if already set
      appointmentStatus:
        prev?.appointmentStatus ??
        appointment["Appointment Status"] ??
        "Active",
      startTime,
      endTime,
      notes: appointment["Note"],
      address: appointment["Appointment Address"],
    }));
  }, [appointment]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      className="w-[35%] min-w-[400px] max-w-[500px] p-[40px]"
    >
      <div className="flex flex-col gap-4 mt-6">
        <div className="flex w-full">
          <div className="w-full">
            <p className="text-sm mb-2">Date:</p>
            <DateInput
              value={rescheduleAppointmentDetails.date}
              handleChange={(e: any) => updateState("date", e ?? "")}
            />
            {displayErrorMsg(
              "Date cannot be empty",
              rescheduleAppointmentDetails.date,
              submitting
            )}
          </div>
        </div>
        <div className="flex justify-between gap-2">
          <div className="flex-1">
            <p className="text-sm mb-2">From:</p>

            <DateInput
              value={rescheduleAppointmentDetails.startTime}
              handleChange={(e) => {
                if (e) {
                  const selectedDate = rescheduleAppointmentDetails.date
                    ? new Date(rescheduleAppointmentDetails.date)
                    : new Date();
                  const selectedTime = new Date(e);

                  selectedDate.setHours(
                    selectedTime.getHours(),
                    selectedTime.getMinutes(),
                    0,
                    0
                  );

                  updateState("startTime", selectedDate);
                }
              }}
              showTimeSelect
              showTimeSelectOnly
              dateFormat="h:mm aa"
            />
            {displayErrorMsg(
              "Start time cannot be empty",
              rescheduleAppointmentDetails.startTime,
              submitting
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm mb-2">To:</p>

            <DateInput
              value={rescheduleAppointmentDetails.endTime}
              handleChange={(e) => {
                if (e) {
                  const selectedDate = rescheduleAppointmentDetails.date
                    ? new Date(rescheduleAppointmentDetails.date)
                    : new Date();
                  const selectedTime = new Date(e);

                  selectedDate.setHours(
                    selectedTime.getHours(),
                    selectedTime.getMinutes(),
                    0,
                    0
                  );

                  updateState("endTime", selectedDate);
                }
              }}
              showTimeSelect
              showTimeSelectOnly
              dateFormat="h:mm aa"
            />
            {displayErrorMsg(
              "End time cannot be empty",
              rescheduleAppointmentDetails.endTime,
              submitting
            )}
          </div>
        </div>
        {/* Appointment Type Selection */}
        <div className="flex w-full gap-4">
          <div className="w-1/2">
            <p className="text-sm mb-2">Category:</p>
            <Dropdown
              onSelect={(s) =>
                updateState("appointmentType", {
                  ...rescheduleAppointmentDetails.appointmentType,
                  category: s,
                })
              }
              options={["Initial", "FollowUp"]}
              className="!h-[40px] !w-full"
              placeholder="Select category"
              value={rescheduleAppointmentDetails.appointmentType.category}
            />
          </div>
          <div className="w-1/2">
            <p className="text-sm mb-2">Mode:</p>
            <Dropdown
              onSelect={(s) =>
                updateState("appointmentType", {
                  ...rescheduleAppointmentDetails.appointmentType,
                  mode: s,
                })
              }
              options={["Online", "Offline"]}
              className="!h-[40px] !w-full"
              placeholder="Select mode"
              value={rescheduleAppointmentDetails.appointmentType.mode}
            />
          </div>
        </div>
        {displayErrorMsg(
          "Both category and mode are required",
          rescheduleAppointmentDetails.appointmentType.category &&
            rescheduleAppointmentDetails.appointmentType.mode,
          submitting
        )}

        {/* Appointment Status Selection */}
        <div className="w-full">
          <p className="text-sm mb-2">Appointment Status:</p>
          <Dropdown
            onSelect={(s) => {
              updateState("appointmentStatus", s);
            }}
            options={["Active", "Missed", "Completed"]}
            className="!h-[40px] !w-full"
            placeholder="Select status"
            value={rescheduleAppointmentDetails.appointmentStatus}
          />
          {displayErrorMsg(
            "Appointment status cannot be empty",
            rescheduleAppointmentDetails.appointmentStatus,
            submitting
          )}
        </div>

        <div className="mb-3 w-full">
          <p className="text-sm mb-2">Appointment Address</p>
          <textarea
            style={{ border: "1px solid", borderColor: colors.grey }}
            onChange={(e) => updateState("address", e.target.value)}
            className="placeholder:text-[#606060] text-[14px] h-[90px] rounded-md outline-none w-full p-2"
            value={rescheduleAppointmentDetails?.address}
          />
        </div>
        <div className="mb-3 w-full">
          <p className="text-sm mb-2">Note</p>
          <textarea
            style={{ border: "1px solid", borderColor: colors.grey }}
            onChange={(e) => updateState("notes", e.target.value)}
            className="placeholder:text-[#606060] text-[14px] h-[90px] rounded-md outline-none w-full p-2"
            value={rescheduleAppointmentDetails?.notes}
          />
        </div>
        <Button
          type="submit"
          text="Edit/Reschedule/Fix FollowUp Appointment/Edit"
          theme="primary"
          size="md"
          className="rounded-md"
          // disabled={!isValid}
          loading={isPending}
          onClick={() => {
            setSubmitting(true);
            rescheduleAppointment({
              ...rescheduleAppointmentDetails,
              patientId: Number(rescheduleAppointmentDetails.patientId),
              doctorId,
              initialAppointmentId:
                rescheduleAppointmentDetails.appointmentType.category ===
                "FollowUp"
                  ? rescheduleAppointmentDetails.initialAppointmentId
                  : undefined, // Only include if FollowUp
              address: rescheduleAppointmentDetails.address,
            });
          }}
        />
      </div>
    </Modal>
  );
};

export default RescheduleAppointmentModal;
